<template>
  <div class="changelog-page">
    <header class="changelog-header">
      <h1>📋 Changelog</h1>
      <p>Track releases and updates across the omni-craft ecosystem</p>
      
      <div class="filters">
        <select v-model="selectedPackage" class="filter-select">
          <option value="">All Packages</option>
          <option value="@orb-zone/cdn-assets">CDN Assets</option>
          <option value="@orb-zone/omni-hub">Omni Hub</option>
          <option value="@orb-zone/e2e-tests">E2E Tests</option>
        </select>
        
        <select v-model="selectedType" class="filter-select">
          <option value="">All Changes</option>
          <option value="major">Breaking Changes</option>
          <option value="minor">Features</option>
          <option value="patch">Fixes</option>
        </select>
      </div>
    </header>

    <main class="changelog-content">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Loading changelog...</p>
      </div>

      <div v-else-if="error" class="error">
        <p>{{ error }}</p>
        <button @click="loadChangelog" class="retry-btn">Try Again</button>
      </div>

      <div v-else class="changelog-entries">
        <article
          v-for="release in filteredReleases"
          :key="release.id"
          class="release"
        >
          <header class="release-header">
            <div class="release-info">
              <h2 class="release-title">
                <span class="package-name">{{ release.packageName }}</span>
                <span class="version-badge" :class="release.type">
                  v{{ release.version }}
                </span>
              </h2>
              <time 
                :datetime="release.date" 
                class="release-date"
              >
                {{ formatDate(release.date) }}
              </time>
            </div>
            
            <div class="release-meta">
              <span class="change-type" :class="release.type">
                {{ formatChangeType(release.type) }}
              </span>
              <a 
                v-if="release.githubUrl" 
                :href="release.githubUrl"
                target="_blank"
                class="github-link"
                aria-label="View release on GitHub"
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16">
                  <path fill="currentColor" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
                </svg>
              </a>
            </div>
          </header>

          <div class="release-content">
            <div 
              v-html="formatMarkdown(release.description)"
              class="release-description"
            ></div>
            
            <ul v-if="release.changes.length" class="change-list">
              <li 
                v-for="change in release.changes"
                :key="change.id"
                class="change-item"
              >
                <span class="change-emoji">{{ getChangeEmoji(change.type) }}</span>
                <span class="change-text">{{ change.description }}</span>
                <a 
                  v-if="change.commitUrl"
                  :href="change.commitUrl"
                  target="_blank"
                  class="commit-link"
                >
                  {{ change.shortSha }}
                </a>
              </li>
            </ul>
          </div>
        </article>

        <div v-if="!filteredReleases.length" class="empty-state">
          <p>No releases found matching your filters.</p>
          <button @click="clearFilters" class="clear-filters-btn">
            Clear Filters
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

interface ChangelogRelease {
  id: string;
  packageName: string;
  version: string;
  type: 'major' | 'minor' | 'patch';
  date: Date;
  description: string;
  changes: ChangelogEntry[];
  githubUrl?: string;
  author: string;
}

interface ChangelogEntry {
  id: string;
  type: 'added' | 'changed' | 'deprecated' | 'removed' | 'fixed' | 'security';
  description: string;
  commitUrl?: string;
  shortSha?: string;
}

const releases = ref<ChangelogRelease[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const selectedPackage = ref('');
const selectedType = ref('');

const filteredReleases = computed(() => {
  return releases.value.filter(release => {
    const packageMatch = !selectedPackage.value || release.packageName === selectedPackage.value;
    const typeMatch = !selectedType.value || release.type === selectedType.value;
    return packageMatch && typeMatch;
  });
});

const loadChangelog = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    // In a real implementation, this would fetch from your API
    // For now, we'll simulate loading from git tags and changeset files
    const response = await fetch('/api/changelog');
    
    if (!response.ok) {
      throw new Error('Failed to load changelog');
    }
    
    const data = await response.json();
    releases.value = data.releases.map((release: any) => ({
      ...release,
      date: new Date(release.date)
    }));
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load changelog';
  } finally {
    loading.value = false;
  }
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

const formatChangeType = (type: string): string => {
  const types = {
    major: 'Breaking Change',
    minor: 'Feature',
    patch: 'Fix'
  };
  return types[type as keyof typeof types] || type;
};

const getChangeEmoji = (type: string): string => {
  const emojis = {
    added: '✨',
    changed: '🔄',
    deprecated: '⚠️',
    removed: '🗑️',
    fixed: '🐛',
    security: '🔒'
  };
  return emojis[type as keyof typeof emojis] || '📝';
};

const formatMarkdown = (text: string): string => {
  // Simple markdown formatting - in production, use a proper markdown parser
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>');
};

const clearFilters = () => {
  selectedPackage.value = '';
  selectedType.value = '';
};

onMounted(() => {
  loadChangelog();
});
</script>

<style scoped>
.changelog-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
}

.changelog-header {
  text-align: center;
  margin-bottom: 3rem;
}

.changelog-header h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  color: #333;
}

.changelog-header p {
  margin: 0 0 2rem 0;
  color: #666;
  font-size: 1.1rem;
}

.filters {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.filter-select {
  padding: 0.5rem 1rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: white;
  font-size: 0.9rem;
  min-width: 150px;
}

.filter-select:focus {
  outline: none;
  border-color: #667eea;
}

.loading {
  text-align: center;
  padding: 3rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

.retry-btn {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.changelog-entries {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.release {
  background: white;
  border-radius: 12px;
  border: 1px solid #e1e5e9;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.release-header {
  background: #f8f9fa;
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.release-info h2 {
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.package-name {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 1.1rem;
  color: #333;
}

.version-badge {
  background: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Monaco', 'Menlo', monospace;
}

.version-badge.major {
  background: #dc3545;
  color: white;
}

.version-badge.minor {
  background: #28a745;
  color: white;
}

.version-badge.patch {
  background: #ffc107;
  color: #212529;
}

.release-date {
  color: #6c757d;
  font-size: 0.9rem;
}

.release-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.change-type {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.change-type.major {
  background: #fff5f5;
  color: #dc3545;
}

.change-type.minor {
  background: #f0fff4;
  color: #28a745;
}

.change-type.patch {
  background: #fffbf0;
  color: #ffc107;
}

.github-link {
  color: #6c757d;
  text-decoration: none;
  transition: color 0.2s;
}

.github-link:hover {
  color: #333;
}

.release-content {
  padding: 1.5rem;
}

.release-description {
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #555;
}

.change-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.change-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.change-item:last-child {
  border-bottom: none;
}

.change-emoji {
  flex-shrink: 0;
  font-size: 1.1rem;
}

.change-text {
  flex: 1;
  line-height: 1.5;
}

.commit-link {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
  color: #667eea;
  text-decoration: none;
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  flex-shrink: 0;
}

.commit-link:hover {
  background: #e9ecef;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .changelog-page {
    padding: 1rem;
  }
  
  .release-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filters {
    flex-direction: column;
    align-items: center;
  }
  
  .filter-select {
    width: 100%;
    max-width: 300px;
  }
}
</style>